import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ShoppingCart } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { mockProducts } from '../data/mock';
import { useCart } from '../contexts/CartContext';
import { useToast } from '../hooks/use-toast';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { toast } = useToast();
  const product = mockProducts.find(p => p.id === id);

  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [quantity, setQuantity] = useState(1);

  if (!product) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-3xl font-bold mb-4">Product Not Found</h2>
          <Button onClick={() => navigate('/shop')} className="bg-[#F4B400] text-black hover:bg-[#d49f00]">
            Back to Shop
          </Button>
        </div>
      </div>
    );
  }

  const handleAddToCart = () => {
    if (!selectedSize || !selectedColor) {
      toast({
        title: "Please Select Options",
        description: "Please select size and color before adding to cart.",
        variant: "destructive"
      });
      return;
    }
    addToCart(product, selectedSize, selectedColor, quantity);
    toast({
      title: "Added to Cart",
      description: `${product.name} has been added to your cart.`,
    });
  };

  return (
    <div className="bg-black text-white pt-24">
      <section className="py-12 bg-gradient-to-br from-black to-gray-900">
        <div className="container mx-auto px-6">
          <Button
            onClick={() => navigate('/shop')}
            variant="outline"
            className="border-[#F4B400] text-[#F4B400] hover:bg-[#F4B400] hover:text-black mb-8"
          >
            <ArrowLeft className="mr-2" size={16} />
            Back to Shop
          </Button>
        </div>
      </section>

      <section className="py-16 bg-white text-black">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 max-w-6xl mx-auto">
            {/* Product Image */}
            <div>
              <img 
                src={product.image} 
                alt={product.name}
                className="w-full rounded-lg shadow-2xl"
              />
            </div>

            {/* Product Info */}
            <div>
              <h1 className="text-4xl font-bold mb-4">{product.name}</h1>
              <p className="text-3xl font-bold text-[#F4B400] mb-6">${product.price}</p>
              <p className="text-gray-600 text-lg mb-8 leading-relaxed">{product.description}</p>

              {/* Size Selection */}
              <div className="mb-6">
                <label className="block font-semibold mb-3">Size</label>
                <Select value={selectedSize} onValueChange={setSelectedSize}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select size" />
                  </SelectTrigger>
                  <SelectContent>
                    {product.sizes.map(size => (
                      <SelectItem key={size} value={size}>{size}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Color Selection */}
              <div className="mb-6">
                <label className="block font-semibold mb-3">Color</label>
                <Select value={selectedColor} onValueChange={setSelectedColor}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select color" />
                  </SelectTrigger>
                  <SelectContent>
                    {product.colors.map(color => (
                      <SelectItem key={color} value={color}>{color}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Quantity */}
              <div className="mb-8">
                <label className="block font-semibold mb-3">Quantity</label>
                <div className="flex items-center gap-4">
                  <Button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    variant="outline"
                    className="w-12 h-12"
                  >
                    -
                  </Button>
                  <span className="text-2xl font-bold w-12 text-center">{quantity}</span>
                  <Button
                    onClick={() => setQuantity(quantity + 1)}
                    variant="outline"
                    className="w-12 h-12"
                  >
                    +
                  </Button>
                </div>
              </div>

              {/* Add to Cart */}
              <Button
                onClick={handleAddToCart}
                disabled={!product.inStock}
                size="lg"
                className="w-full bg-[#F4B400] text-black hover:bg-[#d49f00] text-lg py-6 transition-all duration-300"
              >
                <ShoppingCart className="mr-2" size={20} />
                {product.inStock ? 'Add to Cart' : 'Out of Stock'}
              </Button>

              {/* Product Details */}
              <Card className="mt-8">
                <CardContent className="p-6">
                  <h3 className="font-bold text-lg mb-3">Product Details</h3>
                  <ul className="space-y-2 text-gray-600">
                    <li>• Category: {product.category}</li>
                    <li>• Available Sizes: {product.sizes.join(', ')}</li>
                    <li>• Available Colors: {product.colors.join(', ')}</li>
                    <li>• {product.inStock ? 'In Stock' : 'Out of Stock'}</li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProductDetail;